import posthog, { type CaptureOptions } from 'posthog-js';
import type { EventName, EventProperties } from '../eventNames';
import { isPostHogConfigured } from './config';

type PendingEvent = {
  name: EventName;
  properties: EventProperties[EventName];
  options?: CaptureOptions;
};

const MAX_PENDING_EVENTS = 100;

// 初回描画では子コンポーネントの effect が Provider の初期化 effect より先に実行される。
// その短い未初期化期間に発生した手動イベントを失わないよう、初期化完了までメモリ上に保持する。
const pendingEvents: PendingEvent[] = [];

const captureEvent = ({ name, properties, options }: PendingEvent): void => {
  if (options) {
    posthog.capture(name, properties, options);
  } else {
    posthog.capture(name, properties);
  }
};

/**
 * PostHog 初期化前に保留した手動イベントを送信する。
 * identify/reset より先に送ると会員イベントが匿名ユーザーに紐づくため、Provider の識別処理後に呼び出す。
 */
export const flushPendingEvents = (): void => {
  if (!posthog.__loaded) return;

  const events = pendingEvents.splice(0);
  events.forEach(captureEvent);
};

/**
 * 手動イベント送信ラッパー（最小セット用）。
 *
 * ⚠️ クリック / ページ遷移 / フォーム submit はここで送らない（Autocapture に任せる）。
 * 使うのは {@link EVENT_NAMES} に定義した手動イベント最小セットのみ。
 *
 * イベント名とプロパティ型は {@link EventProperties} で紐付くため、
 * 未定義イベントや不正なプロパティはコンパイルエラーになる。
 *
 * @param options posthog.capture のオプション。離脱時など「ページが閉じる瞬間でも確実に送りたい」
 *   イベントは `{ send_instantly: true, transport: 'sendBeacon' }` を渡す。
 *   既定のバッチキューだと unload に間に合わず欠損するため、即時 beacon 送信に切り替える。
 */
export function trackEvent<E extends EventName>(
  name: E,
  properties: EventProperties[E],
  options?: CaptureOptions,
): void {
  // 保留経路を通るのは初回描画で init が終わる前に発生したイベントだけ。
  // __loaded は init 後ずっと true のままなので、以降の呼び出しは保留を挟まず即送信される。
  if (!posthog.__loaded) {
    // KEY 未設定のテスト・未構築環境では、送信されないイベントを溜め続けない。
    if (!isPostHogConfigured()) return;
    // 初期化が完了しない場合もメモリ使用量が増え続けないよう、古いイベントから破棄する。
    if (pendingEvents.length >= MAX_PENDING_EVENTS) pendingEvents.shift();
    pendingEvents.push({ name, properties, options });
    return;
  }

  captureEvent({ name, properties, options });
}
